import { useState } from "react";
import { Search, Loader2, PartyPopper, MapPin, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LeadForm } from "@/components/LeadForm";
import { COMPANY } from "@/lib/constants";

// DMV ZIP detection: DC (200xx, 202-205xx), Maryland (206-219xx), Virginia (201xx, 220-246xx)
function isDmvZip(zip) {
  const n = parseInt(zip, 10);
  if (Number.isNaN(n)) return false;
  const dc = n >= 20001 && n <= 20599;
  const md = n >= 20600 && n <= 21999;
  const va = n >= 20100 && n <= 24699;
  return dc || md || va;
}

export const ZipChecker = () => {
  const [zip, setZip] = useState("");
  const [checking, setChecking] = useState(false);
  const [open, setOpen] = useState(false);
  const [served, setServed] = useState(true);

  const check = (e) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(zip)) return;
    setChecking(true);
    // brief "thinking" moment for a delightful, reassuring experience
    setTimeout(() => {
      setServed(isDmvZip(zip));
      setChecking(false);
      setOpen(true);
    }, 1200);
  };

  return (
    <>
      <div data-testid="zip-checker" className="bg-white text-foreground rounded-2xl shadow-xl p-4 sm:p-6 w-full sm:w-[360px]">
        <div className="flex items-center gap-2 text-primary">
          <MapPin className="w-5 h-5" />
          <p className="font-heading font-bold text-base sm:text-lg leading-none">Do we serve your area?</p>
        </div>
        <p className="text-sm text-muted-foreground mt-1.5">Enter your ZIP code to check availability across the DMV.</p>
        <form onSubmit={check} className="flex gap-2 mt-3 sm:mt-4">
          <input
            data-testid="zip-input"
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
            placeholder="e.g. 22003"
            aria-label="ZIP code"
            className="flex-1 min-w-0 h-12 sm:h-13 py-3 px-4 rounded-xl border-2 border-input text-lg font-semibold tracking-wide focus:outline-none focus:border-primary"
          />
          <button
            type="submit"
            data-testid="zip-check-btn"
            disabled={checking || zip.length !== 5}
            className="grid place-items-center w-12 sm:w-13 h-12 sm:h-13 shrink-0 rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground transition-colors disabled:opacity-50"
            aria-label="Check availability"
          >
            {checking ? <Loader2 className="w-6 h-6 animate-spin" /> : <Search className="w-6 h-6" />}
          </button>
        </form>
        {checking && <p className="text-sm text-muted-foreground mt-2 animate-pulse">Checking availability…</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="zip-result-dialog">
          <DialogHeader>
            <DialogTitle className="sr-only">Availability Result</DialogTitle>
            <DialogDescription className="sr-only">Service availability result for your ZIP code, with a form to request care.</DialogDescription>
          </DialogHeader>
          {served ? (
            <div className="text-center">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-primary/10 text-primary mx-auto">
                <PartyPopper className="w-8 h-8" />
              </span>
              <h3 data-testid="zip-success-msg" className="font-heading font-extrabold text-3xl text-primary mt-4">
                Great news — we serve {zip}!
              </h3>
              <p className="text-muted-foreground mt-2">
                Esprit Home Healthcare provides care in your area. Tell us a little about your needs and our team will reach out right away.
              </p>
            </div>
          ) : (
            <div className="text-center">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-accent/15 text-accent mx-auto">
                <MapPin className="w-8 h-8" />
              </span>
              <h3 data-testid="zip-outside-msg" className="font-heading font-extrabold text-2xl text-primary mt-4">
                Let's confirm coverage for {zip}
              </h3>
              <p className="text-muted-foreground mt-2">
                We primarily serve the DMV and are always expanding. Leave your details or call us and we'll let you know how we can help.
              </p>
              <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 text-primary font-semibold mt-3">
                <Phone className="w-5 h-5" /> {COMPANY.phone}
              </a>
            </div>
          )}
          <div className="mt-5 border-t border-border pt-5">
            <LeadForm source={`zip-${zip}`} zip={zip} compact />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
